import { FC, useCallback, useEffect, useRef } from 'react';
import { Theme as MuiTheme, useMediaQuery } from '@mui/material';
import { StyledCanvas, StyledOuterWrapper, StyledWrapper } from './styles';
import {
  canvasHeaderWrapperId,
  headerHeight,
  zoom2HeaderHeight,
} from '../../../constants';

import { HeaderProps } from './types';
import { Theme } from '../../../styles';
import Topbar from './Topbar';
import { drawHeader } from '../../../utils/drawHeader/drawHeader';
import { getCanvasWidth } from '../../../utils/getCanvasWidth';
import { resizeCanvas } from '../../../utils/resizeCanvas';
import { useCalendar } from '../../../context/CalendarProvider';
import { useTheme } from 'styled-components';
import { useTranslate } from '../../../../../locales';

const Header: FC<HeaderProps> = ({
  zoom,
  topBarWidth,
  showThemeToggle,
  toggleTheme,
}) => {
  const translate = useTranslate();
  const week = translate('admin.week');
  const { date, cols, dayOfYear, startDate } = useCalendar();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isSmall = useMediaQuery((theme: MuiTheme) =>
    theme.breakpoints.down('sm'),
  );

  const theme = useTheme() as Theme;

  const handleResize = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      const width = getCanvasWidth();
      const currentHeaderHeight = zoom === 2 ? zoom2HeaderHeight : headerHeight;
      const height = currentHeaderHeight + 1;
      resizeCanvas(ctx, width, height);

      drawHeader(ctx, zoom, cols, startDate, week, dayOfYear, theme);
    },
    [cols, dayOfYear, startDate, week, zoom, theme],
  );

  useEffect(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    const onResize = () => handleResize(ctx);
    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, [handleResize]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.style.letterSpacing = '1px';
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    handleResize(ctx);
  }, [date, zoom, handleResize]);

  return (
    <StyledOuterWrapper>
      {isSmall ? null : (
        <Topbar
          width={topBarWidth}
          showThemeToggle={showThemeToggle}
          toggleTheme={toggleTheme}
        />
      )}
      <StyledWrapper id={canvasHeaderWrapperId}>
        <StyledCanvas ref={canvasRef} />
      </StyledWrapper>
    </StyledOuterWrapper>
  );
};

export default Header;
